/**
 * Brand accent inside a heading.
 *
 * The treatment changes with the background, and the reason is measured rather
 * than aesthetic. Amber #E8A33D against the dark green is 6.80:1 — comfortably
 * readable. Against white it is 2.16:1, against cream 1.98:1 and against the
 * wash tint 1.95:1, all below the 3:1 floor that even large text has to clear.
 *
 * So amber becomes *text* only on the dark sections. On light ones the word
 * keeps its normal colour and takes an amber underline instead: the brand still
 * marks the phrase, and the phrase stays legible.
 */
export function Accent({ on = 'dark', children }) {
  if (on === 'dark') {
    return <span className="text-amber">{children}</span>;
  }

  // Inline padding pushes the border clear of descenders — "Kenya" and
  // "You" both drop below the baseline and would otherwise touch the rule.
  return <span className="border-b-[3px] border-amber pb-1">{children}</span>;
}

/**
 * Renders `text` with the first occurrence of `accent` wrapped.
 *
 * Copy stays a plain readable sentence in the content file rather than being
 * pre-split into fragments, so wording can be edited without touching markup.
 * If the accent is not found the heading renders unchanged.
 */
export default function AccentedHeading({ text, accent, on = 'dark' }) {
  const at = accent ? text.indexOf(accent) : -1;

  if (at === -1) return text;

  return (
    <>
      {text.slice(0, at)}
      <Accent on={on}>{accent}</Accent>
      {text.slice(at + accent.length)}
    </>
  );
}
